import Page from 'src/components/Page';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Button,
    Container,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
    createTheme,
    ThemeProvider,
} from '@mui/material';
import { RemoveCircle } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { getDndList, removeDnd } from 'src/utils/dndApi';
import { showErrorToast, showSuccessToast } from 'src/utils/Toast/SnackbarProvider';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';

// Local theme to match the styling of the Companies table.
const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                },
            },
        },
    },
    typography: {
        fontFamily: 'Lexend',
    },
});

export default function DndCompanies() {
    const [dndData, setDndData] = useState([]);
    const [count, setCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);

    const fetchDndList = (page = currentPage, search = searchText) => {
        setIsLoading(true);
        // Backend uses DRF PageNumberPagination (1-based); MUIDataTable pages are 0-based.
        getDndList({ page: page + 1, search })
            .then((response) => {
                // CustomPagination (PageNumberPagination) → { count, next, previous, results }.
                setDndData(response?.data?.results || []);
                setCount(response?.data?.count || 0);
            })
            .catch((error) => {
                showErrorToast(error, 'Failed to load DND companies');
                setDndData([]);
                setCount(0);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchDndList(currentPage, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchText]);

    const handleRemove = (companyId) => {
        setRemovingId(companyId);
        removeDnd(companyId)
            .then(() => {
                showSuccessToast('Company removed from DND list');
                fetchDndList(currentPage, searchText);
            })
            .catch((error) => {
                showErrorToast(error, 'Failed to remove company from DND list');
            })
            .finally(() => {
                setRemovingId(null);
            });
    };

    const columns = [
        {
            name: 'company_name',
            label: 'Company',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const record = dndData[tableMeta.rowIndex];
                    const companyId = record?.company;
                    return companyId ? (
                        <RouterLink
                            to={`/companies/${companyId}`}
                            state={{ fromPage: 'dnd-companies' }}
                            style={{
                                textDecoration: 'none',
                                color: '#00AB55',
                                fontWeight: 'bold',
                            }}
                        >
                            {value}
                        </RouterLink>
                    ) : (
                        <span>{value}</span>
                    );
                },
            },
        },
        {
            name: 'reason_display',
            label: 'Reason',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value || '-'}</span>,
            },
        },
        {
            name: 'note',
            label: 'Note',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value || '-'}</span>,
            },
        },
        {
            name: 'marked_by_name',
            label: 'Added by',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value || '-'}</span>,
            },
        },
        {
            name: 'marked_at',
            label: 'Added on',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <span>{value ? getDDMMYYYYFormat(value) : '-'}</span>,
            },
        },
        {
            name: 'company',
            label: 'Action',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (companyId) => (
                    <Tooltip title="Remove from DND">
                        <span>
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<RemoveCircle />}
                                disabled={removingId === companyId}
                                onClick={() => handleRemove(companyId)}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                Remove from DND
                            </Button>
                        </span>
                    </Tooltip>
                ),
            },
        },
    ];

    const options = {
        selectableRows: 'none',
        filter: false,
        print: false,
        download: false,
        viewColumns: false,
        responsive: 'standard',
        serverSide: true,
        search: true,
        searchOpen: false,
        count,
        rowsPerPage: 10,
        page: currentPage,
        textLabels: {
            body: {
                noMatch: isLoading ? 'Loading…' : 'No companies are on the DND list.',
            },
        },
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'search':
                    setCurrentPage(0);
                    setSearchText(tableState.searchText || '');
                    break;
                default:
                    break;
            }
        },
    };

    return (
        <Page title="DND Companies | CRM">
            <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h4" sx={{ fontFamily: 'Lexend' }}>
                        DND Companies
                    </Typography>
                </Stack>
                {isLoading && <LinearProgress sx={{ mb: 1 }} />}
                <ThemeProvider theme={theme}>
                    <MUIDataTable
                        title="Do-Not-Disturb list"
                        data={dndData}
                        columns={columns}
                        options={options}
                    />
                </ThemeProvider>
            </Container>
        </Page>
    );
}
